import { Input } from 'cd-eft/components/common/Input';
import { observer } from 'mobx-react';
import React, { Fragment, useEffect, useRef } from 'react';
import { FormulaBanner } from '../Common/FormulaBanner';
import { FormulaFormVM } from './FormulaFormVM';
import { toHyphenedLowerCase } from 'cd-eft/utils/stringUtilities';
import { Modal, ModalHeader, ModalBody, ModalFooter, Container } from 'reactstrap';
import { PrintComponent } from 'cd-eft/components/common/PrintPDF';
import { FormulaReport } from 'cd-eft/components/Reports/FormulaReport/FormulaReport';
import { WDTConstructionReport } from 'cd-eft/components/Reports/WDTConstructionReport/WDTConstructionReport';

/*
  The 4 Container components should be imported into each formula form component.
  The FormulaContainerTemplate is meant to be copied as the base of each form 
    component, as seen in Trapezoidal Channel.
  The overall layout has been defined and styled according to the 
    template and exported components so minimal scss modification should be necessary.
*/

interface IInputContainerProps {
  computeCallback: any;
  printCallback: any;
  isLoading: boolean;
  children: any;
  // Optional for now. Really required when implementing Compute/Print functionality
  disableCompute?: boolean;
  disablePrint?: boolean;
  hideButtons?: boolean;
}
// can break this one apart if other sections need unique values
export interface ITemplateContainerProps {
  children: any;
  rowCols?: number;
  extraClass?: string;
}

// Might need to replace Print button with one of our print component buttons when we get to implementing this part.
export const InputContainer = (props: IInputContainerProps) => {
  return (
    <div className="col formula-inputs">
      <div className="formula-inputs-set">{props.children}</div>

      <div className="formula-submit-buttons">
        <span className='submit-label-space'> </span>
        {props.hideButtons ? null :
        <span>
          <button className="usa-button" data-testid='input-container-button' onClick={props.computeCallback} disabled={props.isLoading || props.disableCompute}>Compute</button>
          <button className="usa-button" data-testid='input-container-button' onClick={props.printCallback} disabled={props.disablePrint}>Print</button>
        </span>
        }
      </div>
    </div>
  );
};

export const DescriptionContainer = (props: ITemplateContainerProps) => {
  return <div>{props.children}</div>;
};

export const ImageContainer = (props: ITemplateContainerProps) => {
  return <div>{props.children}</div>;
};

export const ResultsContainer = (props: ITemplateContainerProps) => {
  return (
    <div className="usa-summary-box formula-results-container" role="region">

      <div className="results-container-content usa-summary-box__body">
        <h2 className="results-summary-heading">Results</h2>
        <div className={`row results-value-list row-cols-${props.rowCols ? props.rowCols : 'auto'} ${props.extraClass? props.extraClass : ''}`}>{props.children}</div>
      </div>
    </div>
  );
};


export interface IFormulaPrintModalProps {
  inputs: any[];
  results: any[];
  images: any[];
  vm: any;
  formulaName: string;
  tableData?: boolean;
  stageDischarge?: boolean;
  stageDischargeData?: any;
  windowOptions?: any;
}

export const FormulaPrintModalContainer = observer((props: IFormulaPrintModalProps) => {
  return (
    <Modal className="project-owner-modal" isOpen={props.vm.saveButtonPopup}>
      <ModalBody>
        <PrintComponent
          componentToPrint={FormulaReport}
          printButtonText={'Report'}
          functionProp={props.vm}
          printButtonClasses={"usa-button usa-button--outline"}
          //componentProps is used to pass the calculation results to the pdf
          componentProps={{ inputs: props.inputs, outputs: props.results, images: props.images, formulaName: props.formulaName, tableData: props.tableData, formDescription: props.vm.formDescription, stageDischarge: props.stageDischarge, stageDischargeData: props.stageDischargeData, windowOptions: props.windowOptions}}
        />
      </ModalBody>
    </Modal>
  )
})

export const FormulaLearnMoreHyperLink = observer(()=>{
  return(
    <div>
      
    </div>
  )
})

export interface FormulaFormProps {
  title: string;
}

//-------------------------------------------------------------------------------------------------------------------------
// Don't import this one. Just copy it and inset your code where appropriate.
const FormulaFormTemplateComponent = (props) => {
  // Replace FormulaFormVM with formula specific child class. Should extend FormulaFormVM
  const vm = useRef(new FormulaFormVM(props)).current;
  useEffect(() => {
    vm.getFormData(`${props.section}_${props.title}_${props.id}`);
  },[])

  return (
    <Fragment>
      <FormulaBanner title="Replace Me With Title" help={() => { }} />
      <div className="row formula-content">
        <InputContainer printCallback={() => { }} computeCallback={() => { }} isLoading={vm.isLoading}>
          {/* Inputs Here */}
          <Input label="test" idAndName="exampleInput1" valid={true} value={0} type="number" onChange={() => { }} />
          <Input label="test" idAndName="exampleInput1" valid={true} value={0} type="number" onChange={() => { }} />
        </InputContainer>
        <div className="col formula-information">
          {vm.hasResults ?
            <div className="row formula-results">
              <ResultsContainer>
                <></>
                {/* Insert result columns and rows for needed values here. */}
              </ResultsContainer>
            </div>
            : null}
          <div className="row formula-description">
            <DescriptionContainer>
              <></>
              {/* Description Here. Add in <p> for now. */}
              <p>
                {vm.formDescription}
              </p>
            </DescriptionContainer>
          </div>
          <div className="row formula-images">
            <ImageContainer>
              <></>
              {/* add images in <img> tags. Will need refinement once we actually know how the images are sent to FE */}
              {/* {vm.images.map((img, index) => (
                <img className={`${toHyphenedLowerCase(props.title)}-image-${index}`} src={img} alt={`${props.title} image ${index}`} />
              ))} */}
              {/* <img className="trapezoid-img" src={trapezoidImg} alt="trapezoid-channel-image" /> */}
            </ImageContainer>
          </div>
        </div>
      </div>
    </Fragment>
  );
};
export const FormulaFormTemplate = observer(FormulaFormTemplateComponent);// Remember to replace all these names
//-------------------------------------------------------------------------------------------------------------------------